import { forwardRef, HTMLProps, ReactNode } from 'react'

import { twMerge } from 'tailwind-merge'

import Icon from '../Icon/Icon'
import { IconKey } from '../Icon/util'
import { T } from '../Text/Text'

type Props = {
  description?: ReactNode
  showIcon?: boolean
  title: ReactNode
  variant?: 'info' | 'success' | 'warning' | 'error'
}
const Alert = forwardRef<HTMLDivElement, Props & Omit<HTMLProps<HTMLDivElement>, 'title'>>(
  ({ className, description, showIcon, title, variant = 'info', ...rest }, ref) => {
    let backgroundClassName: string
    let borderClassName: string
    let icon: IconKey
    let iconClassName: string
    let leftMarginClassName: string
    switch (variant) {
      case 'info':
        backgroundClassName = 'bg-blue-50'
        borderClassName = 'border-blue-300'
        icon = 'FaInfoCircle'
        iconClassName = 'text-blue-500'
        leftMarginClassName = 'before:border-blue-500'
        break
      case 'success':
        backgroundClassName = 'bg-green-50'
        borderClassName = 'border-green-300'
        icon = 'FaCheckCircle'
        iconClassName = 'text-green-500'
        leftMarginClassName = 'before:border-green-500'
        break
      case 'warning':
        backgroundClassName = 'bg-yellow-50'
        borderClassName = 'border-yellow-300'
        icon = 'FaExclamationTriangle'
        iconClassName = 'text-yellow-500'
        leftMarginClassName = 'before:border-yellow-500'
        break
      case 'error':
        backgroundClassName = 'bg-red-50'
        borderClassName = 'border-red-300'
        icon = 'FaExclamationCircle'
        iconClassName = 'text-red-500'
        leftMarginClassName = 'before:border-red-500'
        break
    }
    return (
      <div
        className={twMerge(
          `relative grid w-full bg-white ${showIcon ? 'grid-cols-[auto_1fr]' : 'grid-cols-1'} items-baseline gap-x-2 gap-y-1 rounded-md border px-4 py-3 ${borderClassName} before:pointer-events-none before:absolute before:top-[-1px] before:bottom-[-1px] before:left-[-1px] before:h-[calc(100%_+_2px)] before:w-full before:border-l-4 before:content-[''] ${leftMarginClassName} before:rounded-tl-md before:rounded-bl-md ${backgroundClassName}`,
          className
        )}
        ref={ref}
        role="alert"
        {...rest}
      >
        {showIcon && (
          <T.Body className={`${description ? 'row-span-2' : ''} ${iconClassName}`}>
            <Icon icon={icon} />
          </T.Body>
        )}
        {typeof title === 'string' ? <T.Body>{title}</T.Body> : title}
        {!!description &&
          (typeof description === 'string' ? (
            <T.BodySmall>{description}</T.BodySmall>
          ) : (
            description
          ))}
      </div>
    )
  }
)

export default React.memo(Alert)
